import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import axios from 'axios';
import config from 'constants/apiConfig';
import { selectGroupGuid } from '../../../slices/commonSelectors';

export type SectionTaskScope = 'org' | 'incident';

export type SectionTaskLink = {
  section_id: string;
  action_id: string;
  scope: SectionTaskScope;
  source: 'generated' | 'pinned';
  created_by: string | null;
  created_at: string;
};

export type SectionTaskLinkWithReport = SectionTaskLink & {
  sitrep_id: string;
  sitrep_name: string | null;
  sitrep_incident_id: string | null;
  sitrep_aor_id: string | null;
  section_name: string | null;
};

export const sectionTaskLinkKeys = {
  base: ['sectionTaskLinks'] as const,
  bySection: (sectionId: string | undefined) =>
    [...sectionTaskLinkKeys.base, 'section', sectionId] as const,
  byActions: (groupGuid: string | undefined, actionIds: string[]) =>
    [
      ...sectionTaskLinkKeys.base,
      'actions',
      groupGuid,
      actionIds.join(','),
    ] as const,
};

const linksUrl = (sitrepId: string, sectionId: string) =>
  `${config.apiGateway.sitrepsV2}/${sitrepId}/sections/${sectionId}/tasks/links`;

export const useSectionTaskLinks = (
  sitrepId: string | undefined,
  sectionId: string | undefined
) =>
  useQuery({
    queryKey: sectionTaskLinkKeys.bySection(sectionId),
    queryFn: async () => {
      const response = await axios.get<{ links: SectionTaskLink[] }>(
        linksUrl(sitrepId as string, sectionId as string)
      );
      return response.data?.links ?? [];
    },
    enabled: Boolean(sitrepId && sectionId),
  });

export const usePinSectionTasks = (sitrepId: string, sectionId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      links: Array<{ action_id: string; scope: SectionTaskScope }>
    ) => {
      const response = await axios.post<{ inserted: number }>(
        linksUrl(sitrepId, sectionId),
        { links }
      );
      return response.data;
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: sectionTaskLinkKeys.base }),
  });
};

export const useUnlinkSectionTask = (sitrepId: string, sectionId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (actionId: string) => {
      const response = await axios.delete<{ removed: boolean }>(
        `${linksUrl(sitrepId, sectionId)}/${actionId}`
      );
      return response.data;
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: sectionTaskLinkKeys.base }),
  });
};

/**
 * Which reports generated or pinned the given tasks (for the Hub's "Generated
 * from report" link).
 */
export const useTaskReportLinks = (actionIds: string[]) => {
  const groupGuid = useSelector(selectGroupGuid) as string | undefined;
  const ids = [...new Set(actionIds.filter(Boolean))].sort();
  return useQuery({
    queryKey: sectionTaskLinkKeys.byActions(groupGuid, ids),
    queryFn: async () => {
      const response = await axios.get<{
        links: SectionTaskLinkWithReport[];
      }>(`${config.apiGateway.sitrepsV2}/taskLinks`, {
        params: { actionIds: ids.join(','), group_guid: groupGuid },
      });
      return response.data?.links ?? [];
    },
    enabled: Boolean(groupGuid) && ids.length > 0,
    staleTime: 30_000,
  });
};
